// hooks/useAutomationMetadata.ts
import { useState, useEffect } from 'react';
import { getRules, getActions } from 'wisestep-api';

import { JobAutomation, RulesetResponse } from './AutomationInterface';

export interface ParamMetadata {
  id: string;
  name: string;
  displayName: string;
  type: string;
  required: boolean;
  defaultValue?: any;
  allowedValues?: string[];
  allowedValuesWithLabels?: Record<string, string>;
  validationRegex?: string;
  validationMessage?: string;
  hints?: any;
  operatorMetadata?: any;
}

export interface RuleMetadata {
  id: string;
  name: string;
  description: string;
  ruleType?: string;
  params: Record<string, ParamMetadata>;
}

export interface ActionMetadata {
  id: string;
  name: string;
  description: string;
  actionType?: string;
  params: Record<string, ParamMetadata>;
}

export interface RuleMetadataCollection {
  [key: string]: RuleMetadata;
}

export interface ActionMetadataCollection {
  [key: string]: ActionMetadata;
}

interface UseAutomationMetadataResult {
  ruleMetadata: RuleMetadataCollection;
  actionMetadata: ActionMetadataCollection;
  ruleParameterMap: Record<string, Record<string, ParamMetadata>>;
  actionParameterMap: Record<string, Record<string, ParamMetadata>>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAutomationMetadata = (): UseAutomationMetadataResult => {
  const [ruleMetadata, setRuleMetadata] = useState<RuleMetadataCollection>({});
  const [actionMetadata, setActionMetadata] = useState<ActionMetadataCollection>({});
  const [ruleParameterMap, setRuleParameterMap] = useState<
    Record<string, Record<string, ParamMetadata>>
  >({});
  const [actionParameterMap, setActionParameterMap] = useState<
    Record<string, Record<string, ParamMetadata>>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const transformRuleApiResponse = (apiResponse: any): RuleMetadataCollection => {
    const metadata: RuleMetadataCollection = {};

    if (apiResponse.success && apiResponse.rules) {
      apiResponse.rules.forEach((rule: any) => {
        const ruleKey = rule.ruleName;
        metadata[ruleKey] = {
          id: rule.ruleId,
          name: rule.ruleName,
          description: rule.ruleDescription,
          params: {},
        };

        if (rule.params && Array.isArray(rule.params)) {
          rule.params.forEach((param: any) => {
            const paramKey = param.name;
            metadata[ruleKey].params[paramKey] = {
              id: param.paramId,
              name: param.name,
              displayName: param.displayName,
              type: param.type,
              required: param.required,
              defaultValue: param.defaultValue,
              allowedValues: param.allowedValues,
              allowedValuesWithLabels: param.allowedValuesWithLabels,
              hints: param.hints,
              operatorMetadata: param.operatorMetadata,
            };
          });
        }
      });
    }
    return metadata;
  };

  const transformActionApiResponse = (apiResponse: any): ActionMetadataCollection => {
    const metadata: ActionMetadataCollection = {};

    if (apiResponse.success && apiResponse.actions) {
      apiResponse.actions.forEach((action: any) => {
        const actionKey = action.actionName;
        metadata[actionKey] = {
          id: action.actionId,
          name: action.actionName,
          description: action.actionDescription,
          // actionType: action.actionType,
          params: {},
        };

        if (action.params && Array.isArray(action.params)) {
          action.params.forEach((param: any) => {
            const paramKey =
              param.name ||
              param.displayName
                ?.replace(/\s+/g, '')
                .replace(/^./, (str: string) => str.toLowerCase());
            metadata[actionKey].params[paramKey] = {
              id: param.paramId || param.id,
              name: param.name,
              displayName: param.displayName,
              type: param.type,
              required: param.required,
              defaultValue: param.defaultValue,
              allowedValues: param.allowedValues || [],
              allowedValuesWithLabels: param.allowedValuesWithLabels || {},
              hints: param.hints,
              validationRegex: param.validationRegex,
              validationMessage: param.validationMessage,
              operatorMetadata: param.operatorMetadata,
            };
          });
        }
      });
    }
    return metadata;
  };

  const createRuleParameterMap = (apiResponse: any) => {
    const paramMap: Record<string, Record<string, ParamMetadata>> = {};
    if (apiResponse.success && apiResponse.rules) {
      apiResponse.rules.forEach((rule: any) => {
        const ruleKey = rule.ruleName;
        paramMap[ruleKey] = {};

        if (rule.params && Array.isArray(rule.params)) {
          rule.params.forEach((param: any) => {
            const paramKey = param.name;
            paramMap[ruleKey][paramKey] = {
              id: param.paramId,
              name: param.name,
              displayName: param.displayName,
              type: param.type,
              required: param.required,
              defaultValue: param.defaultValue,
              allowedValues: param.allowedValues,
              allowedValuesWithLabels: param.allowedValuesWithLabels,
              hints: param.hints,
              operatorMetadata: param.operatorMetadata,
            };
          });
        }
      });
    }
    return paramMap;
  };

  const createActionParameterMap = (apiResponse: any) => {
    const paramMap: Record<string, Record<string, ParamMetadata>> = {};
    if (apiResponse.success && apiResponse.actions) {
      apiResponse.actions.forEach((action: any) => {
        const actionKey = action.actionName;
        paramMap[actionKey] = {};

        if (action.params && Array.isArray(action.params)) {
          action.params.forEach((param: any) => {
            const paramKey =
              param.name ||
              param.displayName
                ?.replace(/\s+/g, '')
                .replace(/^./, (str: string) => str.toLowerCase());
            paramMap[actionKey][paramKey] = {
              id: param.paramId || param.id,
              name: param.name,
              displayName: param.displayName,
              type: param.type,
              required: param.required,
              defaultValue: param.defaultValue,
              allowedValues: param.allowedValues || [],
              allowedValuesWithLabels: param.allowedValuesWithLabels || {},
              hints: param.hints,
              validationRegex: param.validationRegex,
              validationMessage: param.validationMessage,
              operatorMetadata: param.operatorMetadata,
            };
          });
        }
      });
    }
    return paramMap;
  };

  const fetchMetadata = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rulesResponse, actionsResponse] = await Promise.all([getRules(), getActions()]);
      const transformedRuleMetadata = transformRuleApiResponse(rulesResponse.data);
      const ruleParamMap = createRuleParameterMap(rulesResponse.data);
      const transformedActionMetadata = transformActionApiResponse(actionsResponse.data);
      const actionParamMap = createActionParameterMap(actionsResponse.data);

      setRuleMetadata(transformedRuleMetadata);
      setActionMetadata(transformedActionMetadata);
      setRuleParameterMap(ruleParamMap);
      setActionParameterMap(actionParamMap);
    } catch (err: any) {
      console.error('Error fetching metadata:', err);
      setError(err?.message || 'Failed to fetch automation metadata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
  }, []);

  return {
    ruleMetadata,
    actionMetadata,
    ruleParameterMap,
    actionParameterMap,
    loading,
    error,
    refetch: fetchMetadata,
  };
};

export const transformToSelectArray = (
  allowedValues?: string[],
  allowedValuesWithLabels?: Record<string, string>
) => {
  if (!allowedValues) return [];

  return allowedValues.map((value) => ({
    label: allowedValuesWithLabels?.[value] || value,
    value: value,
  }));
};

export const findParameterByDisplayName = (
  params: Record<string, ParamMetadata>,
  searchTerm: string
): ParamMetadata | undefined => {
  return Object.values(params).find((p) =>
    p.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

export const transformRulesetToJobAutomation = (ruleset: RulesetResponse): JobAutomation => {
  const trigger =
    ruleset.eventTypes && ruleset.eventTypes.length > 0
      ? `${ruleset.eventTypes.join(', ')} on ${ruleset.entityType}`
      : `Event on ${ruleset.entityType}`;

  const conditions =
    ruleset.rules.length > 0
      ? `${ruleset.rules.map((rule) => rule.ruleName).join(', ')}`
      : 'No rules defined';

  const actions =
    ruleset.actions.length > 0
      ? `${ruleset.actions.map((action) => action.actionName).join(', ')}`
      : 'No actions defined';

  return {
    id: ruleset.id,
    automationName: ruleset.name,
    trigger,
    conditions,
    actions,
    status: ruleset.active ? 'Active' : 'Inactive',
    entityType: ruleset.entityType,
    createdBy: ruleset.createdBy,
    createdDate: ruleset.createdDate,
  };
};
